import { useCallback, useEffect, useRef } from 'react'

export const useInfiniteScroll = ({
  isLoading,
  hasMore,
  loadMore,
}: {
  isLoading: boolean
  hasMore: boolean
  loadMore: () => void
}) => {
  const observer = useRef<IntersectionObserver | null>(null)

  const lastItemRef = useCallback(
    (node: Element | null) => {
      if (isLoading) {
        return
      }
      if (observer.current) {
        observer.current.disconnect()
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry.isIntersecting && hasMore) {
            // Stop observing this element to avoid multiple loadMore calls for the same visibility event
            observer.current?.unobserve(entry.target)
            loadMore()
          }
        },
        { threshold: 1.0 },
      )

      if (node) {
        observer.current.observe(node)
      }
    },
    [isLoading, hasMore, loadMore],
  )

  useEffect(() => {
    return () => {
      observer.current?.disconnect()
    }
  }, [])

  return { lastItemRef }
}
